import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle, ArrowUpRight } from "lucide-react";
import { ADDRESS, EMAIL, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_LINK, REQUIREMENT_TYPES, DOMAIN } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", business_name: "", email: "", phone: "", requirement_type: REQUIREMENT_TYPES[0], message: "" };

const inputCls =
    "w-full bg-transparent border-b border-line focus:border-gold outline-none py-3 text-navy placeholder:text-slate-400 transition-colors duration-300 text-sm md:text-base";

export const Contact = () => {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/inquiries`, form);
            toast.success("Inquiry received. Our factory team will reach out within 24 hours.");
            setForm(EMPTY);
        } catch (err) {
            toast.error("Could not send your inquiry. Please WhatsApp us instead.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="bg-white py-24 lg:py-32 border-t border-line">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5"
                >
                    <p className="font-mono text-[11px] tracking-[0.35em] text-gold mb-6" data-testid="contact-overline">
                        06 / BULK INQUIRY
                    </p>
                    <h2 className="font-heading font-extrabold tracking-tighter text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="contact-heading">
                        GET A FACTORY-DIRECT QUOTE.
                    </h2>
                    <p className="mt-6 text-muted text-base md:text-lg leading-relaxed">
                        Share your floor plan or requirement — we respond with pricing, materials and a
                        delivery timeline within one business day.
                    </p>

                    <div className="mt-12 space-y-6" data-testid="contact-details">
                        <div className="flex items-start gap-4">
                            <MapPin size={18} className="text-gold mt-1 shrink-0" />
                            <div>
                                <p className="font-mono text-[10px] tracking-[0.3em] text-muted">FACTORY & SHOWROOM</p>
                                <p className="text-navy font-medium mt-1" data-testid="contact-address">{ADDRESS}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <Phone size={18} className="text-gold mt-1 shrink-0" />
                            <div>
                                <p className="font-mono text-[10px] tracking-[0.3em] text-muted">CALL / WHATSAPP</p>
                                <a href={PHONE_TEL} className="text-navy font-medium mt-1 block hover:text-gold transition-colors" data-testid="contact-phone">
                                    {PHONE_DISPLAY}
                                </a>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <Mail size={18} className="text-gold mt-1 shrink-0" />
                            <div>
                                <p className="font-mono text-[10px] tracking-[0.3em] text-muted">EMAIL / WEB</p>
                                <p className="text-navy font-medium mt-1" data-testid="contact-email">{EMAIL} · {DOMAIN}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <Clock size={18} className="text-gold mt-1 shrink-0" />
                            <div>
                                <p className="font-mono text-[10px] tracking-[0.3em] text-muted">HOURS</p>
                                <p className="text-navy font-medium mt-1">Mon – Sat · 9:30 AM – 7:30 PM</p>
                            </div>
                        </div>
                    </div>

                    <a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white font-medium tracking-wide hover:bg-navy transition-colors duration-300"
                        data-testid="whatsapp-cta-button"
                    >
                        <MessageCircle size={18} />
                        Instant Quote on WhatsApp
                        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </a>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-7 bg-bone border border-line p-8 md:p-12"
                    data-testid="inquiry-form"
                >
                    <div className="grid md:grid-cols-2 gap-x-8 gap-y-8">
                        <div>
                            <label className="font-mono text-[10px] tracking-[0.3em] text-muted">NAME *</label>
                            <input required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} data-testid="form-name-input" />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] tracking-[0.3em] text-muted">BUSINESS NAME</label>
                            <input value={form.business_name} onChange={set("business_name")} placeholder="Company / firm" className={inputCls} data-testid="form-business-input" />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] tracking-[0.3em] text-muted">EMAIL *</label>
                            <input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} data-testid="form-email-input" />
                        </div>
                        <div>
                            <label className="font-mono text-[10px] tracking-[0.3em] text-muted">PHONE NUMBER *</label>
                            <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} data-testid="form-phone-input" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="font-mono text-[10px] tracking-[0.3em] text-muted">REQUIREMENT TYPE *</label>
                            <select value={form.requirement_type} onChange={set("requirement_type")} className={`${inputCls} cursor-pointer`} data-testid="form-requirement-select">
                                {REQUIREMENT_TYPES.map((r) => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="font-mono text-[10px] tracking-[0.3em] text-muted">MESSAGE</label>
                            <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Seats required, floor size, timeline, delivery location..." className={`${inputCls} resize-none`} data-testid="form-message-textarea" />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="group mt-10 inline-flex items-center gap-2 px-10 py-4 bg-navy text-white font-medium tracking-wide hover:bg-gold disabled:opacity-60 transition-colors duration-300"
                        data-testid="form-submit-button"
                    >
                        {loading ? "Sending..." : "Request Bulk Quote"}
                        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </button>
                    <p className="mt-4 text-xs text-muted" data-testid="form-note">
                        Your details go straight to our factory sales desk — never to dealers.
                    </p>
                </motion.form>
            </div>
        </section>
    );
};
